/**
 * Generic paginated list for the admin console. Given an itemType it looks up
 * the matching AdminAPI/TrainingDataAPI fetcher in fetchConfig, loads items
 * (training-data types are fetched unpaginated and shown on one page, sorted by
 * label for stability), and renders each row as an AdminListItem with numbered
 * pagination controls, loading/error/empty states, and an optional onItemClick.
 * Exposes refresh() to parents through a forwarded PaginatedListRef.
 * Used by AdminListViewContainer and UsersAdminPage under /admin.
 */
import { useState, useEffect, forwardRef, useImperativeHandle, useCallback } from "react";
import { SubmissionVerificationTrainingDataSetDisplay } from "@/types/trainingData";
import { ChevronLeft, ChevronRight } from "lucide-react";
import LoadingSpinner from "./LoadingSpinner";
import { Pagination } from "@/types/pagination";
import { AdminAPI } from "@/api/admin";
import { AdminListItem, ListItem, ListItemType } from "../pages/admin/AdminListItem";
import { TrainingDataAPI } from "@/api/aiTrainingData";
import { DataType } from "@/types/trainingData";

export interface PaginatedListProps<T extends ListItem> {
    itemType: ListItemType;
    itemsPerPage?: number;
    className?: string;
    onItemClick?: (item: T) => void;
    dataType?: DataType;
}

export interface PaginatedListRef {
    refresh: () => void;
}

const fetchConfig: Record<ListItemType, { fetch: Function; prop?: string }> = {
    ai_bounty: { fetch: AdminAPI.fetchAiBounties, prop: "aiBounties" },
    ai_submission: { fetch: AdminAPI.fetchAiSubmissions, prop: "aiSubmissions" },
    bounty: { fetch: AdminAPI.fetchBounties, prop: "bounties" },
    submission: { fetch: AdminAPI.fetchSubmissions, prop: "submissions" },
    payment: { fetch: AdminAPI.fetchPayments, prop: "paymentBountySubmissions" },
    user: { fetch: AdminAPI.fetchUsers, prop: "users" },
    // Training data types return arrays directly (no pagination wrapper)
    submission_verification_training_data_set: {
        fetch: TrainingDataAPI.fetchSubmissionVerificationTrainingData,
    },
    search_training_data_set: {
        fetch: TrainingDataAPI.fetchSearchTrainingData,
    },
};

const PaginatedListComponent = <T extends ListItem>(
    {
        itemType,
        itemsPerPage = 10,
        className,
        onItemClick,
        dataType = "submission_verification",
    }: PaginatedListProps<T>,
    ref: React.Ref<PaginatedListRef>
) => {
    const [items, setItems] = useState<T[]>([]);
    const [pagination, setPagination] = useState<Pagination>({
        currentPage: 1,
        perPage: itemsPerPage,
        totalCount: 0,
        pageCount: 0,
        hasNext: false,
        hasPrev: false,
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const getItemKey = (item: T, index: number) => {
        // Use a stable key per item so React won't treat order changes as insertions/removals.
        // When the backend sends rows in a different sequence, array indices would shift and
        // cause the list to visually "jump"; deriving a consistent key avoids that.
        if (item && typeof item === "object") {
            const candidate = item as unknown as Record<string, unknown>;
            if ("id" in candidate && candidate.id !== null && candidate.id !== undefined) {
                return String(candidate.id);
            }
            if ("label" in candidate && typeof candidate.label === "string") {
                return `label-${candidate.label}`;
            }
            if ("uuid" in candidate && typeof candidate.uuid === "string") {
                return candidate.uuid;
            }
        }
        return `${itemType}-${index}`;
    };

    const fetchItems = useCallback(
        async (page: number = 1) => {
            setLoading(true);
            try {
                const config = fetchConfig[itemType as ListItemType];
                if (config) {
                    // These types don't use pagination - they return full lists
                    const isNonPaginated =
                        itemType === "submission_verification_training_data_set" ||
                        itemType === "search_training_data_set";

                    if (isNonPaginated) {
                        // Fetch all items without pagination params
                        const fetchedItems = (await config.fetch()) as T[];
                        let processedItems = fetchedItems;

                        if (itemType === "submission_verification_training_data_set") {
                            // Keep rows stable between refreshes: incoming data order can vary,
                            // so sort by label to avoid React reordering the list on each fetch.
                            processedItems = [...fetchedItems].sort((a, b) => {
                                const aLabel =
                                    (a as SubmissionVerificationTrainingDataSetDisplay).label ?? "";
                                const bLabel =
                                    (b as SubmissionVerificationTrainingDataSetDisplay).label ?? "";
                                return aLabel.localeCompare(bLabel);
                            });
                        }

                        setItems(processedItems);
                        // Create pagination state for all items
                        setPagination({
                            currentPage: 1,
                            perPage: processedItems.length || 1,
                            totalCount: processedItems.length,
                            pageCount: 1,
                            hasNext: false,
                            hasPrev: false,
                        });
                    } else {
                        // Normal paginated fetch
                        const response = await config.fetch({ page, perPage: itemsPerPage });
                        if (config.prop) {
                            setItems(response[config.prop]);
                            setPagination(response.pagination);
                        }
                    }
                } else {
                    setItems([]);
                    setPagination({
                        currentPage: 1,
                        perPage: itemsPerPage,
                        totalCount: 0,
                        pageCount: 0,
                        hasNext: false,
                        hasPrev: false,
                    });
                }
            } catch (err) {
                console.error("Error fetching paginated list:", err);
                setError(err instanceof Error ? err.message : "Failed to load data");
            } finally {
                setLoading(false);
            }
        },
        [itemType, itemsPerPage, dataType]
    );

    useEffect(() => {
        fetchItems(1);
    }, [fetchItems]);

    // Expose refresh method via ref
    useImperativeHandle(
        ref,
        () => ({
            refresh: () => {
                fetchItems(pagination.currentPage);
            },
        }),
        [fetchItems, pagination.currentPage]
    );

    const handlePageChange = (newPage: number) => {
        if (newPage < 1 || newPage > pagination.pageCount || loading) return;
        fetchItems(newPage);
    };

    const renderPaginationControls = () => {
        if (pagination.pageCount <= 1) return null;
        const pageNumbers = [];
        const maxVisiblePages = 5;
        let startPage = Math.max(1, pagination.currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(pagination.pageCount, startPage + maxVisiblePages - 1);
        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }
        for (let i = startPage; i <= endPage; i++) {
            pageNumbers.push(i);
        }
        return (
            <div className="flex items-center justify-center space-x-2 mt-4">
                <button
                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                    disabled={!pagination.hasPrev || loading}
                    className="p-2 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    aria-label="Previous page"
                >
                    <ChevronLeft className="w-5 h-5" />
                </button>
                {startPage > 1 && (
                    <>
                        <button
                            onClick={() => handlePageChange(1)}
                            disabled={loading}
                            className="px-3 py-1 rounded hover:bg-gray-100 disabled:opacity-50"
                        >
                            1
                        </button>
                        {startPage > 2 && <span className="px-2">...</span>}
                    </>
                )}
                {pageNumbers.map((page) => (
                    <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        disabled={loading}
                        className={`px-3 py-1 rounded ${
                            page === pagination.currentPage
                                ? "bg-gray-900 text-white"
                                : "hover:bg-gray-100"
                        } disabled:opacity-50`}
                    >
                        {page}
                    </button>
                ))}
                {endPage < pagination.pageCount && (
                    <>
                        {endPage < pagination.pageCount - 1 && <span className="px-2">...</span>}
                        <button
                            onClick={() => handlePageChange(pagination.pageCount)}
                            disabled={loading}
                            className="px-3 py-1 rounded hover:bg-gray-100 disabled:opacity-50"
                        >
                            {pagination.pageCount}
                        </button>
                    </>
                )}
                <button
                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                    disabled={!pagination.hasNext || loading}
                    className="p-2 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    aria-label="Next page"
                >
                    <ChevronRight className="w-5 h-5" />
                </button>
            </div>
        );
    };

    if (loading) {
        return (
            <div className={`flex justify-center items-center p-8`}>
                <LoadingSpinner />
            </div>
        );
    }

    if (error) {
        return (
            <div className={`text-center p-8 text-red-600 ${className}`}>
                <p>error: {error}</p>
                <button
                    onClick={() => fetchItems(pagination.currentPage)}
                    className="mt-4 px-4 py-2 bg-gray-900 text-white rounded hover:bg-gray-800"
                >
                    try again
                </button>
            </div>
        );
    }

    if (!items || items.length === 0) {
        return <div className="text-center p-8 text-gray-500">No {itemType}s found</div>;
    }

    return (
        <div className={className}>
            <>
                <div className="mb-4 text-center text-sm text-gray-600">
                    showing {(pagination.currentPage - 1) * pagination.perPage + 1} to{" "}
                    {Math.min(pagination.currentPage * pagination.perPage, pagination.totalCount)}{" "}
                    of {pagination.totalCount} items
                </div>
                <div>
                    {items.map((item, index) => (
                        <AdminListItem
                            key={getItemKey(item, index)}
                            item={item as ListItem}
                            type={itemType}
                            onClick={onItemClick ? () => onItemClick(item) : undefined}
                        />
                    ))}
                </div>
                {renderPaginationControls()}
            </>
        </div>
    );
};

// Use forwardRef with generic type
const PaginatedList = forwardRef(PaginatedListComponent) as <T extends ListItem>(
    props: PaginatedListProps<T> & { ref?: React.Ref<PaginatedListRef> }
) => ReturnType<typeof PaginatedListComponent>;

export default PaginatedList;
